import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  admin_bid_detail,
  admin_live_auction_detail,
} from "../../reduxData/user/userAction";
import AuctionDetailView from "./AuctionDetailView";
import BidDetailView from "./BidDetailView";
import { StatusBadge, Thumb, formatDate, money, pick } from "./shared";

const HIDDEN = ["__v", "images", "image"];
const DATE_KEY = /(_at|At|date|time)$/;
const MONEY_KEY = /(amount|price|fee|total|bid)/i;

const label = (key) =>
  key.replace(/_/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^\w/, (c) => c.toUpperCase());

const renderValue = (key, value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return <pre className="la-json">{JSON.stringify(value, null, 2)}</pre>;
  if (key === "status") return <StatusBadge status={value} />;
  if (DATE_KEY.test(key) && !isNaN(Date.parse(value))) return formatDate(value);
  if (MONEY_KEY.test(key) && typeof value === "number") return money(value);
  return String(value);
};

// type: "auction" | "bid"
const LiveAuctionDetail = ({ type }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDetail = async () => {
      setLoading(true);
      try {
        const res =
          type === "bid" ? await admin_bid_detail(id) : await admin_live_auction_detail(id);
        setDetail(res?.data?.data || null);
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [id, type]);

  const isBid = type === "bid";
  const title = pick(detail, "product_name", "title", "model", "product.product_name") || (isBid ? "Bid" : "Auction");
  const stats = isBid
    ? [
        ["Bid Amount", money(pick(detail, "bid_amount", "amount"))],
        ["Buyer", pick(detail, "buyer_name", "buyer_email", "buyer.email") || "-"],
        ["Placed At", formatDate(pick(detail, "created_at", "createdAt"))],
      ]
    : [
        ["Seller", pick(detail, "seller_name", "seller_email", "seller.email") || "-"],
        ["Starts", formatDate(pick(detail, "start_time", "start_date"))],
        ["Ends", formatDate(pick(detail, "end_time", "end_date"))],
      ];

  const fields = detail
    ? Object.entries(detail).filter(([k]) => !HIDDEN.includes(k))
    : [];

  return (
    <div className="container la-page">
      <button className="la-back" onClick={() => navigate(-1)}>
        <i className="bi bi-arrow-left"></i>Back
      </button>
      {loading ? (
        <div className="d-grid gap-3">
          <div className="la-skel-card"><div className="s-media" style={{ height: 260 }} /></div>
          <div className="la-stats">
            {[0, 1, 2, 3].map((n) => <div key={n} className="la-skel-card"><div className="s-body"><div className="s-line" /><div className="s-line" style={{ width: "60%" }} /></div></div>)}
          </div>
        </div>
      ) : detail && !isBid ? (
        <AuctionDetailView detail={detail} id={id} />
      ) : detail && isBid ? (
        <BidDetailView detail={detail} />
      ) : detail ? (
        <>
          <div className="la-card la-hero">
            <Thumb src={pick(detail, "images.0", "image", "product.images.0")} />
            <div className="flex-grow-1">
              <div className="la-sub mb-1">{isBid ? "Buyer Bid" : "Seller Auction"}</div>
              <h4>{title}</h4>
              <StatusBadge status={detail?.status} />
            </div>
            {!isBid && (
              <button
                className="btn btn-primary btn-sm"
                onClick={() => navigate(`/buyer-bids?auction_id=${id}`)}
              >
                <i className="bi bi-cash-coin me-1"></i>View Bids
              </button>
            )}
          </div>
          <div className="la-stats">
            {stats.map(([k, v]) => (
              <div key={k} className="la-card la-stat">
                <div className="k">{k}</div>
                <div className="v">{v}</div>
              </div>
            ))}
          </div>
          <div className="la-card">
            <div className="la-section-title">All details</div>
            <div className="la-fields">
              {fields.map(([k, v]) => (
                <div key={k} className="la-field">
                  <div className="k">{label(k)}</div>
                  <div className="v">{renderValue(k, v)}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <div className="la-card la-empty">
          <i className="bi bi-inbox"></i>No record found
        </div>
      )}
    </div>
  );
};

export default LiveAuctionDetail;
